import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import { Column, ColumnKeys, FILTERS, PREDEFINED_FILTERS_KEYS } from './grid-config';
import { ExchangeAntispamRule } from '@nirvana/shared/dtos/types/policies/common';
import { GzExchangeAntispamRulesGridDataProvider } from './GzExchangeAntispamRulesGridDataProvider';
import { getGridColumnDetails, GRID_COLUMNS } from './grid-config';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTISPAM_RULES_GRID } from '../../../../../../../../webcomponents.constants';
import { customElement, query } from 'lit/decorators.js';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { html, TemplateResult } from 'lit';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { ExchangeAntispamRuleFormValue } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { PolicyGridEvents } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/events/events';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';
import { DefaultPolicyColumnKeys } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView/configs/column-config';
import { GzInputNumberWebComponent } from '../../../../../../../../ui/gzInput';
import { GzExchangeAntispamRulesFormWebComponent } from './rulesItemForm';
import { nameCellCustomRenderer } from '../../../antimalware/gridComponents/filteringRules/grid-config';
import { PropertyValues } from 'lit/development';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTISPAM_RULES_GRID)
export class GzExchangeAntispamRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ExchangeAntispamRule,
  ExchangeAntispamRuleFormValue,
  GzExchangeAntispamRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;

  protected showHeaderCustomActionsInsideGrid = true;
  //filters
  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected hasPriorityColumn = true;
  protected hasMoveToTopAction = true;
  protected hasCloneRowAction = true;

  protected translations = {
    editRow: translateFn('Edit priority'),
    deleteRow: translateFn('Delete'),
    moveToTop: translateFn('Move to top'),
    cloneRow: translateFn('Clone'),
  };

  protected columnRenderers = {
    [ColumnKeys.RULE_NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
  };
  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#clone-button')
  protected cloneButton: GzButtonWebComponent;

  protected getDataProvider(): GzExchangeAntispamRulesGridDataProvider {
    return new GzExchangeAntispamRulesGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.updateComplete.then(() => {
      const gridToolbar = this.gridComponent.shadowRoot?.querySelector('.grid-header__toolbar') as HTMLElement;
      gridToolbar.style.paddingTop = '0px';
    });
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button id="clone-button" type="${ButtonTypes.OUTLINE}" @click="${this.onCloneButtonClick.bind(this)}"
        >${translateFn('Clone')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete rules?'),
          translateFn('You are about to delete the selected rules. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  private onCloneButtonClick(): void {
    if (!this.hasSelectableRows) {
      return;
    }

    // a single item can be cloned
    const itemPosition = this.selectedItemsGridData[0];
    const item = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items[itemPosition - 1];

    this.cloneRule(item);
  }

  private cloneRule(item: ExchangeAntispamRule): void {
    // set max priority
    const maxPriority = this.getMaxPriority(1);
    const addRuleCallback = (formValue: ExchangeAntispamRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create new item
    const newItem: ExchangeAntispamRuleFormValue = {
      ...item,
      defaultPositionInGrid: 1, // by default, a new rule will be added at the top of the list
      name: this.getSuggestedNameForClonedItem(item.name, 'name'),
      default: false,
      active: true,
    };

    const ruleNames = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items.map(
      rule => rule.name
    );
    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntispamRulesFormWebComponent(maxPriority, ruleNames, newItem),
      strings: {
        title: translateFn('Add antispam filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onAddButtonClick(): void {
    // get max priority based on default rule if it exists
    const maxPriority = this.getMaxPriority(1);

    const addRuleCallback = (formValue: ExchangeAntispamRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    const ruleNames = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items.map(
      rule => rule.name
    );
    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntispamRulesFormWebComponent(maxPriority, ruleNames),
      strings: {
        title: translateFn('Add antispam filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: ExchangeAntispamRuleFormValue): void {
    // get max priority based on default rule if it exists
    const maxPriority = this.getMaxPriority(item.default ? 1 : 0);

    const addRuleCallback = (formValue: ExchangeAntispamRuleFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    const ruleNames = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items
      .map(rule => rule.name)
      .filter(name => name !== item.name);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntispamRulesFormWebComponent(maxPriority, ruleNames, item),
      strings: {
        title: translateFn('Edit antispam filtering rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };
    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  /**
   * Get max priority based on default rule if it exists.
   * @param offset - offset to be added to the max priority (when adding a new rule, offset is 1)
   * @private
   */
  private getMaxPriority(offset: number): number {
    const items = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items;

    // get max priority
    const maxPriority = items.length + offset;
    // get default rule index - if no default rule, this will be -1
    const defaultRuleIndex = items.findIndex(item => item.default);

    // if no default rule, return max priority as it is
    if (defaultRuleIndex === -1) {
      return maxPriority;
    }

    // a new rule should have a priority lower than the default rule
    // Note: when exists, default rule is the last one (checked in production)
    return defaultRuleIndex + offset;
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);

    // check if default rule is selected
    const items = (this.gridComponent.dataProvider as GzExchangeAntispamRulesGridDataProvider).items;
    const isDefaultRuleSelected = event.detail.selectionStoreData.some(
      (selectedItemPosition: number) => items[selectedItemPosition - 1].default
    );

    // buttons should be disabled if default rule is selected
    this.deleteButton.disabled = !event.detail.selectionStoreData.length || isDefaultRuleSelected;
    this.cloneButton.disabled = event.detail.selectionStoreData.length !== 1 || isDefaultRuleSelected;
  }

  /**
   * Override this method to add custom logic for activate inline menu event.
   * Default rule actions are disabled.
   * @param event
   * @protected
   */
  protected onActivateInlineMenu(event: CustomEvent) {
    const item = event.detail.itemData.item as ExchangeAntispamRuleFormValue;

    this.gridComponent.inlineRowActions?.forEach(action => {
      // move to top action is disabled for default rule and the rule that is already at the top
      if (action.value === PolicyGridEvents.MoveToTop) {
        action.disabled = item.default || item.defaultPositionInGrid === 1;
        return;
      }

      // other actions are disabled for default rule
      action.disabled = item.default;
    });
  }

  /**
   * Override this method to add custom logic for edit row enabled event.
   * Max priority is calculated based on the default rule.
   * @param event
   * @protected
   */
  protected onEditRowEnabled(event: CustomEvent) {
    if (!this.hasPriorityColumn) {
      return;
    }

    const currentRowIndex = event.detail.rowData.index;
    const minPriority = 1;
    const maxPriority = this.getMaxPriority(0);

    // set min and max priority for edited row
    const editedCells = this.gridComponent.controller?.view.getAllEditedCellsInRow(currentRowIndex + 2);
    editedCells?.forEach((gridCellEdit: GridCellEditWebComponent) => {
      const cellColumnKey = gridCellEdit.getAttribute('id');

      if (cellColumnKey === DefaultPolicyColumnKeys.POSITION) {
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).min = minPriority;
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).max = maxPriority;
      }
    });
  }

  protected onCloneRow(event: CustomEvent) {
    this.cloneRule(event.detail.rowData.item);
  }
}
